import { useEffect, useMemo, useRef, useState } from 'react';
import { GRADE_SUGERIDA } from '@/agenda/grade';
import {
  VISOES,
  agruparPorDia,
  diasDaSemana,
  limitesDaSemana,
  limitesDoMesVisao,
  navegar,
  resumoDoDia,
  rotuloDaVisao,
  semanasDoMes,
} from '@/agenda/visoes';
import { montarGrade } from '@/agenda/gradeVisao';
import { estadoVisual } from '@/agenda/confirmacao';
import { VISAO_PADRAO, carregarVisao, reiniciarVisaoUmaVez, salvarVisao } from '@/agenda/preferencias';
import DialogoReagendar from '@/components/leads/DialogoReagendar';
import DialogoCancelar from '@/components/agenda/DialogoCancelar';
import { ouvirClinica } from '@/services/clinicasService';
import { ouvirEquipe } from '@/services/usuariosService';
import GradeAgenda from '@/components/agenda/GradeAgenda';
import DetalheAgendamento from '@/components/agenda/DetalheAgendamento';
import DialogoBaixaComparecimento from '@/components/agenda/DialogoBaixaComparecimento';
import DialogoBaixaFalta from '@/components/agenda/DialogoBaixaFalta';
import MesAgenda from '@/components/agenda/MesAgenda';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Carregando from '@/components/Carregando';
import { useAuth } from '@/auth/AuthProvider';
import { ehAvanco } from '@/leads/progresso';
import { ETAPA_AO_CANCELAR } from '@/agenda/cancelamento';
import {
  limitesDoDia,
  alternarConfirmacao,
  buscarAgendamentos,
  ouvirAgendamentos,
  registrarComparecimento,
} from '@/services/agendamentosService';
import { buscarLead, ouvirLeads, renomearLead } from '@/services/leadsService';
import { buscarConversaDoLead } from '@/services/conversasService';
import { canalDaConversa } from '@/conversas/canais';
import { guardarEstado } from '@/lib/estadoDeSessao';
import { filtrarAgendamentos } from '@/agenda/busca';
import { nomeCurto } from '@/equipe/nomeCurto';
import AvisoCarga from '@/components/AvisoCarga';

const paraDia = (data) => format(data, 'yyyy-MM-dd');

/**
 * A agenda de avaliação, em grade horária (VF-3).
 *
 * ★ DIA E SEMANA USAM A MESMA GRADE — o dia é a semana com uma coluna. Duas
 * representações do mesmo fato poderiam discordar na tela; uma só, não.
 *
 * A visão por TURNOS da VF-2 saiu como layout e sobreviveu como conta: o resumo
 * do cabeçalho de cada coluna vem de `resumoDoDia`, a mesma função que alimenta
 * as células. Cabeçalho e grade não podem divergir porque nascem juntos.
 */
export default function AgendaPage() {
  // ★ WT lote 1 — o que não carregou, para a tela avisar em vez de mostrar menos.
  const [falhaClinica, setFalhaClinica] = useState(false);
  const [falhaEquipe, setFalhaEquipe] = useState(false);
  const { clinicaId, usuario, perfil, papel } = useAuth();
  // ★ `irPara`, e NÃO `navegar`: este arquivo já importa `navegar` de
  // `@/agenda/visoes` (a função pura que avança a data). Chamar os dois pelo
  // mesmo nome fez o `const` do componente SOMBREAR o import, e a navegação de
  // data virou `navigate('dia')` → /agenda/dia → "Página não encontrada".
  // O lint agora barra a categoria (`no-shadow`), mas o nome certo evita a
  // pergunta.
  const irPara = useNavigate();
  // ★ A visão volta como a pessoa deixou. Preferência de TELA (não dado de
  // paciente), no localStorage COM O UID na chave — o computador da recepção é
  // compartilhado. Ver `agenda/preferencias.js`.
  const [visao, definirVisao] = useState(VISAO_PADRAO);
  const preferenciaLida = useRef(false);
  const [clinica, setClinica] = useState(null);
  const [doPeriodo, setDoPeriodo] = useState([]);
  const [dia, setDia] = useState(() => paraDia(new Date()));
  const [agendamentos, setAgendamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [horarioSugerido, setHorarioSugerido] = useState(null);
  const [reagendando, setReagendando] = useState(null);
  const [cancelando, setCancelando] = useState(null);
  // ★★ WI: a baixa "Compareceu" passou a ter DIÁLOGO. `baixando` guarda
  // { consulta, lead } — o lead vem de uma leitura, porque a Agenda vive fora do
  // `LeadsProvider` (ver `DialogoBaixaComparecimento`).
  const [baixando, setBaixando] = useState(null);
  // ★★ WI: o "Faltou" também ganhou diálogo — pede o PRÓXIMO CONTATO, obrigatoriamente.
  const [faltando, setFaltando] = useState(null);
  // ★ A-3(c) — a busca é texto de tela, não filtro gravado: morre ao sair.
  const [busca, setBusca] = useState('');
  // ★ A-3(b) — o "Abrir conversa" do detalhe: em voo, e o aviso de quando não há conversa.
  const [abrindoConversa, setAbrindoConversa] = useState(false);
  const [avisoConversa, setAvisoConversa] = useState('');

  const setVisao = (nova) => {
    definirVisao(nova);
    salvarVisao(usuario?.uid, nova);
  };

  const autor = { id: usuario?.uid, nome: perfil?.nome ?? usuario?.email ?? 'Usuário', papel };

  // A recepção NÃO marca avaliação: `criarAgendamento` escreve no lead, que as
  // rules reservam a `podeVendas`. Ela confirma, que é escrita só do agendamento.
  const podeVendas = ['gestor', 'sdr', 'superadmin'].includes(papel);

  // Uma vez só, quando o uid existir: depois disso quem manda é a pessoa.
  useEffect(() => {
    if (preferenciaLida.current || !usuario?.uid) return;
    preferenciaLida.current = true;
    /**
     * ★★★ A-3(d) — O REINÍCIO VEM ANTES DA LEITURA, e a ordem é o conserto.
     *
     * ⚠ Trocar `VISAO_PADRAO` para `'semana'` sozinho não mudaria nada para quem
     * já usa a tela: a preferência delas está gravada e `carregarVisao` a
     * devolveria. `reiniciarVisaoUmaVez` descarta a escolha guardada **uma vez
     * por pessoa** (chave versionada própria, `removeItem` de UMA chave — nunca
     * `clear()`), e da segunda abertura em diante a tela volta a lembrar.
     *
     * ⚠⚠ Chamar DEPOIS do `carregarVisao` não teria efeito nenhum nesta
     * abertura: a visão velha já estaria no estado.
     */
    reiniciarVisaoUmaVez(usuario.uid);
    definirVisao(carregarVisao(usuario.uid));
  }, [usuario?.uid]);

  /**
   * ★★ WO lote 3c — a equipe, para o diálogo da baixa oferecer os negociadores.
   *
   * ⚠ Esta tela NÃO vive dentro do `LeadsProvider` (ela é de `TODOS_DA_CLINICA`, a recepção
   * também a abre), então assina por conta própria. ★ A regra de `usuarios` é
   * `membro(clinicaId)`: a recepção lê sem problema, e o botão da baixa continua atrás de
   * `podeVendas`, como a guarda da WI exige.
   *
   * ★★ A-3 ajuste 4 (11/out) — ela passou a ter um SEGUNDO uso: `nomeCurto` precisa
   * da equipe para saber se o primeiro nome do SDR é ambíguo. ⚠ Por isso esta
   * declaração SUBIU para antes de `sdrPorLead`: embaixo, o `useMemo` caía na zona
   * morta do `const` e a tela quebrava com "Cannot access 'equipe' before
   * initialization".
   */
  const [equipe, setEquipe] = useState([]);
  useEffect(() => {
    if (!clinicaId) return undefined;
    // ★★ WT: era `() => {}` — o seletor de SDR da agenda vinha INCOMPLETO em silêncio,
    // o mesmo mecanismo do caso da Jéssica.
    return ouvirEquipe(clinicaId, setEquipe, () => setFalhaEquipe(true));
  }, [clinicaId]);

  /**
   * ★★★ A-3(a) — OS LEADS, **SÓ PARA QUEM É DE VENDAS**. Decisão do dono.
   *
   * ⚠⚠ Medido em 11/out: `agendamentos.sdrId` = **0 de 2.774**. O SDR não está no
   * agendamento, e denormalizá-lo custaria gravar no create + backfill de 2.774
   * documentos + um dado duplicado que divergiria quando o lead trocasse de SDR
   * (a WT já consertou três divergências desse tipo). ★ O caminho escolhido foi
   * cruzar por `leadId` com os leads em memória.
   *
   * ⚠⚠⚠ **A RECEPÇÃO NÃO ENTRA AQUI, e não é otimização: é permissão.** As rules
   * reservam `leads` a `podeVendas` — assinar a coleção para a recepcionista
   * daria `permission-denied` e um aviso de carga na tela dela, por um nome que
   * ela não precisa ver. ★ Há guarda de fonte (`a3.guardas.test.js`) que falha
   * se esta assinatura sair de trás do `podeVendas`.
   *
   * ★ A tela vive FORA do `LeadsProvider` (a rota `/agenda` é de
   * `TODOS_DA_CLINICA`), então assina por conta própria — o mesmo padrão que
   * `ouvirClinica` e `ouvirEquipe` já usam neste arquivo.
   *
   * ⚠ Custo honesto: 8.222 leads (medido em 11/out), os mesmos que o funil já
   * carrega. Para o gestor e a SDR é uma assinatura a mais; para a recepção, zero.
   */
  const [leads, setLeads] = useState([]);
  const [falhaLeads, setFalhaLeads] = useState(false);
  useEffect(() => {
    if (!clinicaId || !podeVendas) return undefined;
    return ouvirLeads(clinicaId, setLeads, () => setFalhaLeads(true));
  }, [clinicaId, podeVendas]);

  /**
   * ★ `leadId → sdrNome`. Medido: 8.212 de 8.222 leads têm `sdrNome` (99,9%);
   * os 10 sem ficam sem linha de SDR na caixinha, que é o certo — inventar
   * "sem SDR" ocuparia espaço para dizer nada.
   */
  const sdrPorLead = useMemo(() => {
    const mapa = new Map();
    for (const l of leads) {
      const nome = String(l?.sdrNome ?? '').trim();
      /**
       * ★★★ A-3 ajuste 4 — o CURTO sai daqui, porque é aqui que a EQUIPE existe.
       *
       * ★ `nomeCurto` precisa da lista de `usuarios` para saber se o primeiro
       * nome é ambíguo ("Ana P." só quando há duas Anas ativas). A agenda já
       * assina a equipe desde a WO lote 3c — ⚠ e é por isso que o cálculo mora
       * na página e não em `gradeVisao`: a função pura da grade não tem (nem
       * deve ter) a equipe.
       */
      if (l?.id && nome) mapa.set(l.id, { curto: nomeCurto(nome, equipe), completo: nome });
    }
    return mapa;
  }, [leads, equipe]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    // ★ WT: era `() => {}` — sem a config, expediente e feriados da agenda somem calados.
    return ouvirClinica(clinicaId, setClinica, () => setFalhaClinica(true));
  }, [clinicaId]);

  const referencia = useMemo(() => new Date(`${dia}T12:00:00`), [dia]);
  const grade = clinica?.config?.agendaAvaliacao ?? GRADE_SUGERIDA;

  /**
   * ★ SEMANA E MÊS USAM LEITURA ÚNICA; só o DIA mantém `onSnapshot`.
   *
   * A diferença não é de estilo. O dia é a tela operacional: a recepção marca
   * comparecimento ali e precisa ver o resultado na hora. Ninguém fica olhando a
   * visão mensal esperando uma consulta mudar de status — um listener aberto
   * sobre um mês ficaria vivo enquanto a pessoa navega, custando por uma
   * reatividade que ninguém usa.
   *
   * ⚠ VF-3: o flag de confirmação ESCREVE, e a semana é onde ele mais será
   * usado. Isso NÃO abre listener e NÃO relê a semana — a atualização é local
   * (`aplicarLocal`). Reler a faixa inteira a cada toque gastaria dezenas
   * de leituras por confirmação, e furaria esta regra pela porta dos fundos.
   */
  useEffect(() => {
    if (!clinicaId || visao === 'dia') return undefined;
    let vivo = true;
    setCarregando(true);
    const janela = visao === 'semana' ? limitesDaSemana(referencia) : limitesDoMesVisao(referencia);
    // A janela do mês precisa cobrir as semanas de borda que o calendário desenha.
    const de = limitesDaSemana(janela.inicio).inicio;
    const ate = limitesDaSemana(janela.fim).fim;
    ate.setHours(23, 59, 59, 999);
    buscarAgendamentos(clinicaId, { de, ate })
      .then((lista) => { if (vivo) { setDoPeriodo(lista); setErro(null); } })
      .catch((e) => {
        console.error('[OdontoSDR] Falha ao buscar o período:', e);
        if (vivo) setErro('Não foi possível carregar o período.');
      })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [clinicaId, visao, referencia]);

  useEffect(() => {
    if (!clinicaId || visao !== 'dia') return undefined;
    setCarregando(true);
    const { inicio, fim } = limitesDoDia(referencia);
    return ouvirAgendamentos(
      clinicaId,
      { de: inicio, ate: fim },
      (lista) => {
        setAgendamentos(lista);
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ouvir a agenda:', e);
        setErro('Não foi possível carregar a agenda.');
        setCarregando(false);
      },
    );
  }, [clinicaId, dia, visao, referencia]);

  const resumo = useMemo(() => {
    const conta = (fn) => agendamentos.filter(fn).length;
    return {
      total: agendamentos.length,
      // "Aguardando" é quem ainda não confirmou — o que sobrou para a recepção
      // ligar. Confirmado sai daqui e ganha indicador próprio.
      aguardando: conta((a) => estadoVisual(a) === 'agendado'),
      confirmados: conta((a) => estadoVisual(a) === 'confirmado'),
      compareceram: conta((a) => a.status === 'compareceu'),
      faltaram: conta((a) => a.status === 'faltou'),
    };
  }, [agendamentos]);

  /**
   * ★★ WI — "COMPARECEU" NÃO GRAVA DIRETO: ABRE O DIÁLOGO.
   *
   * A baixa passou a exigir código, nome confirmado e valor — as mesmas regras do funil
   * (decisão 3 do dono). E como a Agenda vive FORA do `LeadsProvider`, o lead precisa ser
   * LIDO antes, para o diálogo nascer pré-preenchido: sem isso a SDR redigitaria o que o
   * sistema já sabe, que é o desenho que a WH mediu e recusou.
   *
   * ★ `ehAvanco` FALSO → nada a exigir: o lead já está em `compareceu` ou adiante, a etapa
   * não se move, e pedir o código seria cobrar por nada. Medido: 82 das 751 consultas
   * abertas têm o lead já adiante. Nesse caso a baixa segue direto, como antes.
   */
  async function abrirBaixa(original, status) {
    setErro(null);
    setOcupado(true);
    try {
      const lead = original.leadId ? await buscarLead(clinicaId, original.leadId) : null;
      const etapaAlvo = status === 'compareceu' ? 'compareceu' : ETAPA_AO_CANCELAR;
      if (!lead || !ehAvanco(lead.etapa, etapaAlvo)) return false;
      if (status === 'compareceu') setBaixando({ consulta: original, lead });
      else setFaltando({ consulta: original, lead });
      return true;
    } catch (e) {
      setErro(e.message ?? 'Não foi possível abrir a baixa da consulta.');
      return true;
    } finally {
      setOcupado(false);
    }
  }

  async function marcar(caixa, status) {
    const original = [...agendamentos, ...doPeriodo].find((a) => a.id === caixa.id);
    if (!original) return;
    if ((status === 'compareceu' || status === 'faltou') && (await abrirBaixa(original, status))) {
      setAberto(null);
      return;
    }
    setErro(null);
    setOcupado(true);
    try {
      await registrarComparecimento(clinicaId, original, status, autor);
      // ★ A tela tem de mostrar o resultado também onde não há listener.
      aplicarLocal(caixa.id, { status });
      setAberto(null);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível registrar o comparecimento.');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Atualização local de UM agendamento — sem releitura, sem listener novo.
   *
   * ★ UMA FUNÇÃO SÓ, para confirmar e para marcar comparecimento. Antes só o
   * confirmar atualizava a tela: no DIA o `onSnapshot` disfarçava, mas SEMANA e
   * MÊS usam leitura única (a regra da VF-2) e ninguém atualizava `doPeriodo` —
   * marcar "compareceu" na semana deixava a caixinha VERDE, desenhando o estado
   * antigo. Duas ações que fazem a mesma coisa não podem ter dois caminhos.
   */
  const aplicarLocal = (id, campos) => {
    const aplicar = (lista) => lista.map((a) => (a.id === id ? { ...a, ...campos } : a));
    setAgendamentos(aplicar);
    setDoPeriodo(aplicar);
  };

  const camposDaConfirmacao = (marcarConfirmado) => ({
    confirmadoEm: marcarConfirmado ? new Date() : null,
    confirmadoPorId: marcarConfirmado ? autor.id : null,
    confirmadoPorNome: marcarConfirmado ? autor.nome : null,
  });

  async function confirmar(caixa, marcarConfirmado) {
    const original = [...agendamentos, ...doPeriodo].find((a) => a.id === caixa.id);
    if (!original) return;
    setErro(null);
    setOcupado(true);
    // Otimista: o flag precisa responder ao toque. Se a escrita falhar, volta.
    aplicarLocal(caixa.id, camposDaConfirmacao(marcarConfirmado));
    try {
      await alternarConfirmacao(clinicaId, original, marcarConfirmado, autor);
    } catch (e) {
      aplicarLocal(caixa.id, camposDaConfirmacao(!marcarConfirmado));
      setErro(e.message ?? 'Não foi possível alterar a confirmação.');
    } finally {
      setOcupado(false);
    }
  }

  function mudarDia(passo) {
    setDia(paraDia(navegar(visao, referencia, passo)));
  }

  const rotuloDia = format(referencia, "EEEE, d 'de' MMMM", { locale: ptBR });

  /**
   * ★★ A-3(c) — A BUSCA FILTRA ANTES DE MONTAR A GRADE, não depois.
   *
   * ⚠ Filtrar as caixinhas já montadas deixaria as CONTAGENS erradas: o
   * cabeçalho diria "3 de 8 livres" sobre uma coluna onde só uma caixinha
   * aparece. Entrando antes, `montarGrade` reconta `vagas`/`livres` sobre o que
   * sobrou — e a tela não mente sobre a grade.
   *
   * ★ Termo vazio devolve a MESMA lista (identidade), então o `useMemo` da grade
   * não recalcula de graça.
   */
  const doDiaFiltrado = useMemo(() => filtrarAgendamentos(agendamentos, busca), [agendamentos, busca]);
  const doPeriodoFiltrado = useMemo(() => filtrarAgendamentos(doPeriodo, busca), [doPeriodo, busca]);

  // ★ A-3(c) — o MÊS respeita a busca pela mesma razão que a semana: duas visões
  // da mesma janela não podem discordar sobre quem está nela.
  const porDia = useMemo(() => agruparPorDia(doPeriodoFiltrado), [doPeriodoFiltrado]);
  const semanas = useMemo(
    () => (visao === 'mes' ? semanasDoMes(referencia) : []),
    [visao, referencia],
  );

  // A grade do dia é a mesma da semana, com uma coluna só.
  const gradeVisivel = useMemo(() => {
    if (visao === 'dia') return montarGrade(grade, [referencia], doDiaFiltrado, sdrPorLead);
    if (visao === 'semana') return montarGrade(grade, diasDaSemana(referencia), doPeriodoFiltrado, sdrPorLead);
    return null;
  }, [visao, grade, referencia, doDiaFiltrado, doPeriodoFiltrado, sdrPorLead]);

  // ★ A-3(c) — quantos o termo escondeu, para "não achei" não parecer "não há".
  const escondidos = (visao === 'dia' ? agendamentos.length - doDiaFiltrado.length
    : doPeriodo.length - doPeriodoFiltrado.length);

  const irParaDia = (data) => { setDia(paraDia(data)); setVisao('dia'); };

  /**
   * ★★★ A-3(b) — "ABRIR CONVERSA" a partir do agendamento, pelo caminho da WV.
   *
   * ★ O caminho é o mesmo da ficha do lead (`FichaLead.irParaConversa`):
   * `guardarEstado('inbox:selecionada:<canal>', id)` e **depois** navegar — nessa
   * ordem, porque é assim que o `ConversasProvider` nasce com a conversa aberta.
   * Ele a resgata POR ID (conserto da WV), então conversa fora das 200 mais
   * recentes do inbox também abre — exatamente o caso de um agendamento antigo.
   *
   * ⚠⚠ **`['vendas']` EXPLÍCITO, e é o ponto da peça.** O padrão de
   * `buscarConversaDoLead` é "nos dois espaços, a mais recente" (WS) — e aqui
   * isso estaria errado: quem é de vendas tem de cair na conversa DE VENDAS,
   * não na da recepção que por acaso foi tocada ontem.
   *
   * ⚠⚠⚠ **E A RECEPÇÃO NÃO CHEGA AQUI — decisão do dono, 11/out/2026, permanente.**
   * ★★★ *"Quem confirma avaliações é a SDR; a recepção só visualiza a agenda."* ⚠ Não
   * há busca por telefone a implementar: o caminho foi medido (461 de 2.774 por
   * telefone, zero ambiguidade) e **recusado**. ★ O dado concorda com a decisão: das
   * 1.037 conversas da recepção, **0 têm `leadId`**.
   *
   * ★ "Não achei" NÃO é erro vermelho: é aviso. A conversa pode simplesmente não
   * existir (lead que nunca escreveu), e isso não é falha de ninguém. ⚠ Mas
   * recusa de permissão ou índice faltando **é** erro, e `buscarConversaDoLead`
   * LANÇA nesse caso em vez de devolver `null` (regra da WT: "não achei" não pode
   * virar "não existe").
   */
  async function abrirConversa(caixa) {
    if (!caixa?.leadId) return;
    setAvisoConversa('');
    setAbrindoConversa(true);
    try {
      const conversa = await buscarConversaDoLead(clinicaId, caixa.leadId, ['vendas']);
      if (!conversa) {
        setAvisoConversa('Este lead ainda não tem conversa de vendas. Abra a ficha do lead para iniciar uma.');
        return;
      }
      const canal = canalDaConversa(conversa);
      guardarEstado(`inbox:selecionada:${canal}`, conversa.id);
      irPara(`/conversas/${canal}`);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao abrir a conversa do agendamento:', e);
      setAvisoConversa('Não foi possível verificar as conversas deste lead. Tente pela ficha do lead.');
    } finally {
      setAbrindoConversa(false);
    }
  }

  const escolherVaga = (data, horaEscolhida) => {
    // O agendamento nasce no funil (o diálogo pede lead, negociador e orçamento).
    // Daqui a grade leva ao dia com o horário destacado.
    irParaDia(data);
    setErro(null);
    setAberto(null);
    setHorarioSugerido(`${paraDia(data)} ${horaEscolhida}`);
  };

  /**
   * ★★★ A-3 conserto de layout (12/out/2026) — a classe `agenda` liga o MESMO
   * desenho do FUNIL: coluna flex que ocupa a altura restante da tela, com a
   * grade rolando por dentro, nos dois eixos.
   *
   * ⚠⚠ Sem ela a caixa usava `max-height: 82vh` e **passava do fim da tela** —
   * medido em Chrome: `bottom 842` numa janela de 768, com a barra horizontal da
   * própria caixa fora do campo de visão.
   */
  return (
    <div className="pagina agenda">
      <header className="pagina-cabecalho com-acao">
        <div>
          <h1>Agenda</h1>
          <p className="painel-sub">
            {visao === 'dia' ? rotuloDia : rotuloDaVisao(visao, referencia)}
          </p>
          {/* ★★ WT — o que não carregou. ⚠ Sem a config, a agenda desenha o expediente
              errado; sem a equipe, o seletor de SDR vem incompleto. */}
          {falhaClinica && <AvisoCarga oQue="a configuração da clínica" />}
          {falhaEquipe && <AvisoCarga oQue="a equipe" />}
          {/* ★ A-3(a) — sem os leads, a caixinha perde o nome do SDR em SILÊNCIO.
              O aviso é a mesma disciplina da WT: o que não carregou é dito. */}
          {falhaLeads && <AvisoCarga oQue="os leads (o SDR não aparece nas consultas)" />}
        </div>
        <div className="navegacao-dia">
          {/* A visão é ESTADO da /agenda, não rota nova: evita três itens de menu
              para a mesma tela e mantém o App.rotas.test.js intacto. */}
          {VISOES.map((v) => (
            <button
              key={v.id}
              type="button"
              className={visao === v.id ? 'btn-primario' : 'btn-secundario'}
              aria-pressed={visao === v.id}
              onClick={() => setVisao(v.id)}
            >
              {v.label}
            </button>
          ))}
          <button type="button" className="btn-secundario" onClick={() => mudarDia(-1)}>
            ‹ Anterior
          </button>
          <input type="date" value={dia} onChange={(e) => setDia(e.target.value)} />
          <button type="button" className="btn-secundario" onClick={() => mudarDia(1)}>
            Próximo ›
          </button>
          <button
            type="button"
            className="btn-secundario"
            onClick={() => setDia(paraDia(new Date()))}
          >
            Hoje
          </button>
        </div>
      </header>

      {/* ★★ A-3(c) — BUSCA POR NOME E TELEFONE, em memória, sobre o período na tela.
          ⚠ O rótulo diz "neste período" de propósito: a busca não alcança quem está
          em outro mês, e prometer o que não cumpre é pior que não ter busca. */}
      <div className="agenda-busca">
        <input
          type="search"
          value={busca}
          aria-label="Buscar por nome ou telefone neste período"
          placeholder="Buscar por nome ou telefone neste período…"
          onChange={(e) => setBusca(e.target.value)}
        />
        {busca && (
          <>
            <button type="button" className="btn-texto" onClick={() => setBusca('')}>
              limpar
            </button>
            {/* ★ "Nenhuma consulta encontrada" tem de ser diferente de "nenhuma
                consulta no período": sem este contador, buscar um nome que não
                está aqui parece que a agenda está vazia. */}
            <span className="painel-sub">
              {escondidos > 0
                ? `${escondidos} consulta${escondidos > 1 ? 's' : ''} oculta${escondidos > 1 ? 's' : ''} pela busca`
                : 'a busca não escondeu nenhuma consulta'}
            </span>
          </>
        )}
      </div>

      {erro && <p className="erro">{erro}</p>}
      {horarioSugerido && (
        <p className="painel-sub">
          Vaga escolhida: {horarioSugerido.split(' ')[1]}. Marque a consulta pelo funil, no card
          do lead.{' '}
          <button type="button" className="btn-texto" onClick={() => setHorarioSugerido(null)}>
            ok
          </button>
        </p>
      )}

      {visao === 'mes' && (
        carregando ? <Carregando texto="Carregando o mês…" /> : (
          <MesAgenda
            semanas={semanas}
            porDia={porDia}
            resumoDe={(data) => resumoDoDia(grade, data, doPeriodoFiltrado)}
            aoEscolherSemana={(data) => { setDia(paraDia(data)); setVisao('semana'); }}
          />
        )
      )}

      {visao === 'dia' && (
        <div className="indicadores">
          <div className="indicador">
            <span className="indicador-valor">{resumo.total}</span>
            <span className="indicador-rotulo">Consultas</span>
          </div>
          <div className="indicador">
            <span className="indicador-valor">{resumo.aguardando}</span>
            <span className="indicador-rotulo">Aguardando</span>
          </div>
          <div className="indicador">
            <span className="indicador-valor verde">{resumo.confirmados}</span>
            <span className="indicador-rotulo">Confirmados</span>
          </div>
          <div className="indicador">
            <span className="indicador-valor">{resumo.compareceram}</span>
            <span className="indicador-rotulo">Compareceram</span>
          </div>
          <div className="indicador">
            <span className="indicador-valor vermelho">{resumo.faltaram}</span>
            <span className="indicador-rotulo">Faltaram</span>
          </div>
        </div>
      )}

      {visao !== 'mes' &&
        (carregando ? (
          <Carregando texto={visao === 'dia' ? 'Carregando a agenda…' : 'Carregando a semana…'} />
        ) : (
          <GradeAgenda
            grade={gradeVisivel}
            podeAgendar={podeVendas}
            ocupado={ocupado}
            aoAbrirCaixa={setAberto}
            aoAlternarConfirmacao={confirmar}
            aoEscolherVaga={escolherVaga}
            aoEscolherDia={irParaDia}
          />
        ))}

      <DetalheAgendamento
        caixa={aberto}
        aberto={Boolean(aberto)}
        aoFechar={() => { setAberto(null); setAvisoConversa(''); }}
        podeVendas={podeVendas}
        ocupado={ocupado}
        aoAlternar={confirmar}
        aoMarcar={marcar}
        aoReagendar={(caixa) => {
          const original = [...agendamentos, ...doPeriodo].find((a) => a.id === caixa.id);
          setAberto(null);
          setReagendando(original ?? null);
        }}
        aoAbrirLead={(caixa) => irPara(`/funil?lead=${caixa.leadId}`)}
        aoAbrirConversa={abrirConversa}
        avisoConversa={avisoConversa}
        abrindoConversa={abrindoConversa}
        aoCancelar={(caixa) => {
          const original = [...agendamentos, ...doPeriodo].find((a) => a.id === caixa.id);
          setAberto(null);
          setCancelando(original ?? null);
        }}
      />

      <DialogoCancelar
        aberto={Boolean(cancelando)}
        agendamento={cancelando}
        clinicaId={clinicaId}
        config={clinica?.config ?? null}
        autor={autor}
        aoFechar={() => setCancelando(null)}
        aoConcluir={(agendamento) => {
          // Mesma regra dos outros dois caminhos de escrita: a semana não tem
          // listener, então o estado local carrega o que mudou — e só o que
          // mudou. `confirmadoEm` fica: confirmou e cancelou é fato.
          aplicarLocal(agendamento.id, { status: 'cancelado' });
        }}
      />

      <DialogoBaixaFalta
        aberto={Boolean(faltando)}
        lead={faltando?.lead ?? null}
        consulta={faltando?.consulta ?? null}
        clinica={clinica}
        ocupado={ocupado}
        aoFechar={() => setFaltando(null)}
        aoConfirmar={async (retornoEm) => {
          const { consulta, lead } = faltando;
          setOcupado(true);
          try {
            await registrarComparecimento(clinicaId, consulta, 'faltou', autor, { lead, retornoEm });
            aplicarLocal(consulta.id, { status: 'faltou' });
            setFaltando(null);
          } finally {
            setOcupado(false);
          }
        }}
      />

      <DialogoBaixaComparecimento
        aberto={Boolean(baixando)}
        lead={baixando?.lead ?? null}
        consulta={baixando?.consulta ?? null}
        clinica={clinica}
        ocupado={ocupado}
        equipe={equipe}
        usuario={usuario}
        aoFechar={() => setBaixando(null)}
        aoConfirmar={async (dados) => {
          const { consulta, lead } = baixando;
          setOcupado(true);
          try {
            await registrarComparecimento(clinicaId, consulta, 'compareceu', autor, { lead, dados });
            /**
             * ★ A RENOMEAÇÃO vem DEPOIS e em lote próprio, como no `DialogoEtapa`: ela
             * também escreve nas CONVERSAS vinculadas, e um lote não pode consultar.
             * Falhar aqui não desfaz a baixa — e é o certo: a baixa é o fato principal.
             */
            const nomeNovo = String(dados.nome ?? '').trim();
            if (nomeNovo && nomeNovo !== String(lead?.nome ?? '').trim()) {
              await renomearLead(clinicaId, lead, nomeNovo, autor);
            }
            aplicarLocal(consulta.id, { status: 'compareceu' });
            setBaixando(null);
          } finally {
            setOcupado(false);
          }
        }}
      />

      <DialogoReagendar
        aberto={Boolean(reagendando)}
        agendamento={reagendando}
        clinicaId={clinicaId}
        grade={grade}
        autor={autor}
        aoFechar={() => setReagendando(null)}
        aoConcluir={(agendamento, novaData) => {
          /**
           * ★ `dataHora` PRIMEIRO, porque é o único campo que remarcar muda.
           *
           * Antes esta chamada existia e passava `status` e a confirmação — que
           * NÃO tinham mudado — sem a data, que tinha. A caixinha continuava na
           * coluna antiga, e o defeito passou na revisão porque a chamada de
           * `aplicarLocal` estava ali: o que faltava era o campo, não o caminho.
           *
           * Não há releitura da faixa, e não precisa: `montarGrade` deriva a
           * coluna de `chaveDia(dataHora)`, a célula de `horaDe(dataHora)` e
           * reconta `vagas`/`livres` a cada render. Corrigida a data em memória,
           * a caixinha sai da coluna antiga, entra na nova e as duas contagens
           * se ajustam sozinhas — e se a data cair fora da semana exibida,
           * nenhuma coluna casa com a chave e ela simplesmente desaparece.
           */
          aplicarLocal(agendamento.id, {
            dataHora: novaData,
            status: 'agendado',
            confirmadoEm: null,
            confirmadoPorId: null,
            confirmadoPorNome: null,
          });
        }}
      />
    </div>
  );
}
